<!-- src/components/LongRest/LongRestComponent.vue -->
<template>
  <div class="rest-backdrop" :class="{ submitted: submitted }" :style="backdropStyle">
    <div class="rest-panel">
      <span class="rest-corner rest-corner--tl" aria-hidden="true"></span>
      <span class="rest-corner rest-corner--tr" aria-hidden="true"></span>
      <span class="rest-corner rest-corner--bl" aria-hidden="true"></span>
      <span class="rest-corner rest-corner--br" aria-hidden="true"></span>

      <CodexHeading :label="$t('player.longRest.title')" tone="gold" spine="head"/>

      <!-- The offer is created by the cycle, not by the click, so there is a
           window where the panel is open and there is nothing to load yet. -->
      <p v-if="!currentOfferId" class="rest-waiting">
        {{ $t('player.longRest.preparing') }}
      </p>

      <template v-else>
        <div class="rest-meter">
          <div class="rest-meter__track">
            <div class="rest-meter__fill" :class="tierClass" :style="{ width: fillPercent + '%' }"></div>
            <span class="rest-meter__mark rest-meter__mark--half"></span>
            <span class="rest-meter__mark rest-meter__mark--full"></span>
          </div>
          <div class="rest-meter__scale">
            <span>0</span>
            <span class="rest-meter__scale-half">{{ halfCalories }}</span>
            <span>{{ fullCalories }}</span>
          </div>
          <p class="rest-meter__count">
            {{ $t('player.longRest.calories', { current: totalCalories, target: fullCalories }) }}
          </p>
          <p class="rest-meter__tier" :class="tierClass">{{ tierLabel }}</p>
        </div>

        <div class="rest-columns">
          <div class="column pack-column">
            <h3 class="column-label">{{ $t('player.longRest.yourRations') }}</h3>
            <!-- ItemHolder already knows how to render a shelf of world items;
                 the food filter is applied before it ever sees them. -->
            <ItemHolder :items="availableFood" @item-clicked="addProvision"
                        :disabled="submitted" :show-header="false" :overlay="false"
                        :row-count="4"
                        :col-count="3"
            />
            <p class="column-hint">
              {{ availableFood.length
                ? $t('player.longRest.hintAddFood')
                : $t('player.longRest.hintNoFood') }}
            </p>
          </div>

          <div class="column table-column">
            <h3 class="column-label">{{ $t('player.longRest.onTheTable') }}</h3>
            <ItemHolder :items="offeredFood" @item-clicked="removeProvision"
                        :disabled="submitted" :show-header="false" :overlay="false"
                        :row-count="4"
                        :col-count="3"
            />
            <p class="column-hint">
              {{ offeredFood.length
                ? $t('player.longRest.hintTakeBack')
                : $t('player.longRest.hintNothingOffered') }}
            </p>
          </div>
        </div>

        <p v-if="errorKey" class="rest-error">{{ $t(errorKey) }}</p>

        <div class="action">
          <HudButton
              v-if="!submitted"
              :type="ButtonType.SUBMIT"
              :disabled="!canConfirm"
              @click="confirmRest"
          >
            {{ $t('player.longRest.confirm') }}
          </HudButton>
          <HudButton
              :type="ButtonType.CANCEL"
              @click="cancelRest"
          >
            {{ $t('player.longRest.cancel') }}
          </HudButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ItemHolder from "@/components/Item/ItemHolder.vue";
import HudButton from "@/components/btn/HudButton.vue";
import CodexHeading from "@/components/CodexRail/CodexHeading.vue";
import {ButtonType} from "@/types/ButtonType";
import longRestService, {FULL_REST_CALORIES, HALF_REST_CALORIES} from "@/services/LongRestService.js";
import {useLocationBackground} from "@/composables/useLocationBackground";

/* The offer is written when the declared rest resolves on the next phase, so
   a freshly opened panel polls for it rather than assuming it is already there. */
const OFFER_POLL_INTERVAL_MS = 2000;
const OFFER_POLL_ATTEMPTS = 20;

export default {
  name: "LongRestComponent",
  components: {
    ItemHolder,
    HudButton,
    CodexHeading,
  },
  setup(props) {
    /* The backdrop follows where the character is resting, not a coin toss. */
    return useLocationBackground('rest', () => props.position);
  },
  props: {
    // The carried inventory, as world items.
    inventory: {
      type: Array,
      default: () => [],
    },
    // Where the rest is happening, which decides the backdrop.
    position: {
      type: Object,
      default: null,
    },
    // An already-open offer, when the caller knows of one.
    offerId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      ButtonType,
      fullCalories: FULL_REST_CALORIES,
      halfCalories: HALF_REST_CALORIES,
      currentOfferId: this.offerId,
      // World items currently on the table.
      offeredFood: [],
      totalCalories: 0,
      submitted: false,
      errorKey: null,
      pollTimer: null,
      pollsLeft: OFFER_POLL_ATTEMPTS,
    };
  },
  mounted() {
    if (this.currentOfferId) {
      this.loadOffer();
    } else {
      this.pollForOffer();
    }
  },
  beforeUnmount() {
    this.stopPolling();
  },
  watch: {
    offerId(newId) {
      this.currentOfferId = newId;
      this.offeredFood = [];
      this.totalCalories = 0;
      this.submitted = false;
      this.errorKey = null;
      if (newId) this.loadOffer();
    },
  },
  computed: {
    /**
     * Only food may be offered, and only food the character still holds — an
     * item already on the table is not in the pack.
     */
    availableFood() {
      const offered = new Set(this.offeredFood.map(entry => this.worldItemId(entry)));
      return (this.inventory || [])
        .filter(entry => entry?.item?.type === "food")
        .filter(entry => !offered.has(this.worldItemId(entry)));
    },
    fillPercent() {
      return Math.min(100, Math.round((this.totalCalories / this.fullCalories) * 100));
    },
    tierClass() {
      if (this.totalCalories >= this.fullCalories) return "tier-full";
      if (this.totalCalories >= this.halfCalories) return "tier-half";
      return "tier-short";
    },
    tierLabel() {
      if (this.totalCalories >= this.fullCalories) return this.$t("player.longRest.tierFull");
      if (this.totalCalories >= this.halfCalories) return this.$t("player.longRest.tierHalf");
      return this.$t("player.longRest.tierShort");
    },
    canConfirm() {
      return this.totalCalories >= this.halfCalories;
    },
    /**
     * The backdrop bank may not exist yet; without it the panel keeps its own
     * flat wash rather than reaching for a placeholder image.
     */
    backdropStyle() {
      return this.backgroundUrl
        ? {backgroundImage: `url(${this.backgroundUrl})`}
        : {};
    },
  },
  methods: {
    /**
     * The rest action resolves on the next cycle phase, so the offer appears
     * some seconds after the panel does. Give up quietly rather than spin.
     */
    async pollForOffer() {
      try {
        const offers = await longRestService.getOpenOffers();
        const offer = Array.isArray(offers) ? offers[offers.length - 1] : null;
        if (offer) {
          this.currentOfferId = offer.id;
          this.applyOffer(offer);
          this.stopPolling();
          return;
        }
      } catch (error) {
        console.error("Error looking for an open rest offer:", error);
      }
      if (--this.pollsLeft <= 0) {
        this.stopPolling();
        return;
      }
      this.pollTimer = setTimeout(this.pollForOffer, OFFER_POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.pollTimer = null;
    },

    async loadOffer() {
      if (!this.currentOfferId) return;
      try {
        this.applyOffer(await longRestService.getOfferDetails(this.currentOfferId));
      } catch (error) {
        console.error("Error loading rest offer:", error);
      }
    },

    /**
     * The detailed serializer nests the world item under each provision.
     */
    applyOffer(offer) {
      this.offeredFood = (offer.provisions || []).map(p => p.world_item).filter(Boolean);
      this.totalCalories = offer.total_nutrition || 0;
    },

    async addProvision(item) {
      if (this.submitted || !this.currentOfferId) return;
      this.errorKey = null;
      try {
        await longRestService.addProvision(this.currentOfferId, this.worldItemId(item));
        await this.loadOffer();
      } catch (error) {
        console.error("Error adding provision:", error);
      }
    },

    async removeProvision(item) {
      if (this.submitted || !this.currentOfferId) return;
      this.errorKey = null;
      try {
        await longRestService.removeProvision(this.currentOfferId, this.worldItemId(item));
        await this.loadOffer();
      } catch (error) {
        console.error("Error removing provision:", error);
      }
    },

    /**
     * A provision may arrive as a bare id or as the world item itself.
     */
    worldItemId(item) {
      return item && typeof item === "object" ? item.id : item;
    },

    async confirmRest() {
      if (!this.canConfirm) {
        this.errorKey = "player.longRest.notEnough";
        return;
      }
      this.submitted = true;
      try {
        await longRestService.confirmOffer(this.currentOfferId);
        this.$emit("rest-confirmed");
        this.$emit("rest-completed");
      } catch (error) {
        console.error("Error confirming rest:", error);
        this.submitted = false;
        this.errorKey = "player.longRest.notEnough";
      }
    },

    async cancelRest() {
      this.submitted = true;
      try {
        if (this.currentOfferId) await longRestService.cancelOffer(this.currentOfferId);
      } catch (error) {
        console.error("Error cancelling rest:", error);
      }
      this.$emit("rest-cancelled");
      this.$emit("rest-completed");
    },
  },
};
</script>

<style scoped>
/* Same HUD tokens as the bargain panel, so the two overlays read as one system. */
.rest-backdrop {
  --bg-gold: #f2c489;
  --bg-cyan: #3ee9f0;
  --bg-ink: #d3e0e6;
  --bg-dim: #7f929c;
  --bg-rail: rgba(0, 255, 255, 0.18);
  --bg-slab: rgba(11, 18, 24, 0.72);
  --bg-warm: #f0a35a;
  --codex-spine-w: 14px;
  --codex-spine-gap: 0.9rem;
  --codex-spine: rgba(96, 166, 184, 0.85);

  position: fixed;
  top: 0;
  left: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  width: 100%;
  height: 100%;
  padding: 1.5rem 1rem;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  background-color: rgba(2, 6, 9, 0.92);
  background-position: center;
  background-size: cover;
  backdrop-filter: blur(6px) saturate(0.85);
  -webkit-backdrop-filter: blur(6px) saturate(0.85);
}

.rest-backdrop.submitted {
  opacity: 0.5;
  pointer-events: none;
}

.rest-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin: auto;
  padding: 1.1rem 1.35rem 1.35rem;
  color: var(--bg-ink);
  background: linear-gradient(160deg, rgba(12, 19, 25, 0.96) 0%, rgba(6, 10, 14, 0.985) 100%);
  border: 1px solid var(--bg-rail);
  border-radius: 0.375rem;
  box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.72),
      inset 0 1px 0 rgba(62, 233, 240, 0.06);
}

.rest-panel > *:not(.rest-corner) {
  position: relative;
  z-index: 1;
}

.rest-corner {
  position: absolute;
  z-index: 1;
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid rgba(62, 233, 240, 0.45);
  pointer-events: none;
}

.rest-corner--tl { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
.rest-corner--tr { top: -1px; right: -1px; border-width: 1px 1px 0 0; }
.rest-corner--bl { bottom: -1px; left: -1px; border-width: 0 0 1px 1px; }
.rest-corner--br { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

.rest-waiting {
  margin: 0;
  padding: 1.5rem 2rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-dim);
}

/* The meter is the whole rule made visible: two thresholds on one bar. */
.rest-meter {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.55rem 0.7rem 0.65rem;
  background: var(--bg-slab);
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 0.25rem;
}

.rest-meter__track {
  position: relative;
  height: 0.7rem;
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid var(--bg-rail);
  border-radius: 0.15rem;
  overflow: hidden;
}

.rest-meter__fill {
  height: 100%;
  transition: width 160ms ease-out;
}

.rest-meter__fill.tier-short { background: rgba(127, 146, 156, 0.55); }
.rest-meter__fill.tier-half { background: rgba(62, 233, 240, 0.55); }
.rest-meter__fill.tier-full { background: rgba(242, 196, 137, 0.6); }

.rest-meter__mark {
  position: absolute;
  top: 0;
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
}

.rest-meter__mark--half { left: 50%; }
.rest-meter__mark--full { right: 0; }

.rest-meter__scale {
  display: flex;
  justify-content: space-between;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.5rem;
  letter-spacing: 0.08em;
  color: var(--bg-dim);
}

.rest-meter__scale-half {
  position: relative;
  left: 0;
}

.rest-meter__count {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.58rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-ink);
}

.rest-meter__tier {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
}

.rest-meter__tier.tier-short { color: var(--bg-dim); }
.rest-meter__tier.tier-half { color: var(--bg-cyan); }
.rest-meter__tier.tier-full { color: var(--bg-gold); }

.rest-columns {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.7rem 0.75rem;
  background: var(--bg-slab);
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 0.25rem;
}

.table-column {
  border-color: var(--bg-rail);
}

.column-label {
  margin: 0;
  padding-bottom: 0.35rem;
  width: 100%;
  border-bottom: 1px solid var(--bg-rail);
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-gold);
}

.table-column .column-label {
  color: var(--bg-cyan);
  border-bottom-color: rgba(62, 233, 240, 0.45);
}

.column-hint {
  margin: 0;
  min-height: 1rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-dim);
}

.rest-error {
  margin: 0;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.56rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-warm);
}

.action {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding-top: 0.25rem;
}

@media (max-width: 60rem) {
  .rest-columns {
    gap: 0.5rem;
  }
}
</style>
